import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import '../Workflow.scss'
import { useSelector, useDispatch } from 'react-redux'
import HrbTranslate from '../../../../../components/language/HrbTranslate'
import Lang from '../../../../../components/language/services/Lang'
import WorkflowTask from '../../../../../components/library/workflow-tasks/WorkflowTask'
import { useTaskStatus } from '../../hooks/useTaskStatusHook';
import { WorkflowTasks } from '../../../services/WorkflowTasks';
import WorkflowTaskDescriptionWithHelp from '../../../../../components/library/workflow-tasks/WorkflowTaskDescriptionWithHelp';
import Button from '../../../../../components/library/buttons/Button';
import PrepareAdditionalDocuments from './PrepareAdditionalDocuments';
import { TaskStatus } from '../../../services/TaskStatus';
import TaskErrorText from '../../../../../components/library/workflow-tasks/TaskErrorText';
import PrepareReturnApi from '../../../../../services/api/prepareReturnApi';
import PrepareService from '../../../services/prepare/PrepareService';
import TicketActions from '../../../../../store/ticketRedux';
import { createInProgressTaskCompletionObject } from '../../../services/prepare/PrepareUtils';
import { useWorkflowState } from '../../hooks/useWorkflowStateHook';
import WorkflowTaskResetModal from '../../../../../components/library/workflow-tasks/WorkflowTaskResetModal';
import { useInterviewInfo } from '../../hooks/useInterviewInfoHook';
import { useTicketState } from '../../hooks/useTicketStateHook';
import { trackEvent, HeapEvents } from '../../../../../services/util/AnalyticsUtil';
import ExtraMissingFilesError from './ExtraMissingFilesError';
import DoxExplorer from '../../dox/Explorer/doxExplorer';
import DoxUploader from '../../dox/Uploader/doxUploader';

const PrepareReturnTask = (props) => {
    const ticketIsOpen = useTicketState(props.ticket);
    const [baseStatus, completions] = useTaskStatus(props.ticket, [WorkflowTasks.RETURN_PREPARED]);
    const [interviewStatus] = useTaskStatus(props.ticket, [WorkflowTasks.INTERVIEW_COMPLETED]);
    const stateTasks = useWorkflowState(props.ticket);
    const [, , province, , ] = useInterviewInfo(props.ticket)
    const dispatch = useDispatch();
    const prepareReturnApi = PrepareReturnApi.create();

    const [uploaderOpen, setUploaderOpen] = useState(false);
    const [explorerShowing, setExplorerShowing] = useState(false);
    const [hasUploadedFiles, setHasUploadedFiles] = useState(false);
    const [hasUploaderError, setHasUploaderError] = useState(false);
    const [status, setStatus] = useState(TaskStatus.NOT_STARTED);
    const [isCompleting, setIsCompleting] = useState(false);
    const [hasCompletionError, setHasCompletionError] = useState(false);
    const [resetModalShowing, setResetModalShowing] = useState(false);
    const [missingFiles, setMissingFiles] = useState(null);
    const [extraFiles, setExtraFiles] = useState(null)
    const [completionErrorType, setCompletionErrorType] = useState(null)

    const handleUploaderError = () => {
        setHasUploaderError(true);
    }

    useEffect(() => {
        var validCompletion = (completions || []).find(x => x.status !== TaskStatus.INVALID);
        if (validCompletion) {
            var compData = validCompletion.completionData ? JSON.parse(validCompletion.completionData) : {};
            setHasUploadedFiles(compData ? compData.hasUploadedFiles : false)
        }
    }, [])

    useEffect(() => {
        var bStatus = baseStatus;
        if (baseStatus !== TaskStatus.COMPLETE && (stateTasks || []).find(st => st.identifier === WorkflowTasks.RETURN_PREPARED)) {
            bStatus = TaskStatus.IN_PROGRESS
        }

        setStatus(hasUploaderError || hasCompletionError ? TaskStatus.ERROR : bStatus);
    }, [baseStatus, hasUploaderError, stateTasks, hasCompletionError])

    
    const createLink = (urlKey, titleKey) => (
        <a href={Lang.getCurrentValue(urlKey)} target='_blank' rel='noreferrer noopener'>
            <HrbTranslate text={titleKey} />
        </a>
    )

    const createResetLink = () => (
        <a onClick={handleShowModal}><HrbTranslate text='PrepareTask.Step4ResetLinkText'/></a>
    )

    const showUploader = () => {
        setUploaderOpen(true)
    }

    const saveCompletion = async (payload) => {
        if (props.ticket) {
            var response = await prepareReturnApi.updatePrepareReturnTask(props.ticket.id, payload)
            if (response && response.ok) {
                dispatch(TicketActions.getTicket(props.ticket.id))
            }
        }
        setIsCompleting(false);
    }

    const onSavePending = () => {
        setIsCompleting(true)
    }

    const onCloseUploader = () => {
        setUploaderOpen(false);
    }

    const onUploadsComplete = async (filesWereUploaded) => {
        if (filesWereUploaded) {
            setHasUploadedFiles(true);

            var payload = createInProgressTaskCompletionObject(
                (completions || []).find(x => x.status !== TaskStatus.INVALID), null, true);
            saveCompletion(payload);
        }

        setUploaderOpen(false);
    }

    const handleReviewAction = async () => {
        setIsCompleting(true)
        setHasCompletionError(false);
        var response = await PrepareService.completePrepareTask(props.ticket.id, {});
        if (response) {
            if (response.status) {
                dispatch(TicketActions.getTicket(props.ticket.id))
                trackEvent(HeapEvents.COMPLETE_PREPARE_RETURN, props.ticket.id)
            } else {
                setMissingFiles(response.missingFiles)
                setExtraFiles(response.extraFiles)
                if (response.missingFiles && response.extraFiles) {
                    setCompletionErrorType('both')
                } else if (response.missingFiles) {
                    setCompletionErrorType('missing')
                } else if (response.extraFiles) {
                    setCompletionErrorType('extra')
                } else {
                    setCompletionErrorType(null)
                }
                setHasCompletionError(true)
            }
        } else {
            setMissingFiles(null)
            setExtraFiles(null)
            setCompletionErrorType(null)
            setHasCompletionError(true)
        }
        setIsCompleting(false)
    }

    const handleReset = async () => {
        var response = await PrepareService.resetPrepareTask(props.ticket.id);
        if (response) {
            setHasUploadedFiles(false);
            dispatch(TicketActions.getTicket(props.ticket.id))
            trackEvent(HeapEvents.PREPARE_RETURN_RESET, props.ticket.id)
        }
        
        handleHideModal();
    }

    const handleShowModal = () => {
        setResetModalShowing(true);
    }

    const handleHideModal = () => {
        setResetModalShowing(false);
    }

    const showExplorer = () => {
        setExplorerShowing(true)
    }

    const onExplorerClose = () => {
        setExplorerShowing(false)
    }

    
    return (
        <WorkflowTask status={status} taskId={WorkflowTasks.RETURN_PREPARED}>
            <div className='columns'>
                <WorkflowTaskDescriptionWithHelp
                    helpKey='0ProSupport/VDOPro/Prepare.htm'
                    descriptions={['PrepareTask.Step1']}
                    classNames='p1 medium-weight'
                />
            </div>
            <div className='columns prep-section'>
                <div className='column is-offset-1 is-8'>
                    <p className='p2'>
                        <HrbTranslate text={province === 'QC' ? 'PrepareTask.Step1DescQC' : 'PrepareTask.Step1Desc'}
                            params={{
                            RepAClient: createLink('PrepareTask.DescLink1', 'PrepareTask.DescParam1'),
                            ProfAccess: createLink('PrepareTask.DescLink2', 'PrepareTask.DescParam2')
                        }} />
                    </p>
                </div>
            </div>
            <div className='columns'>
                <div className='column is-offset-1 is-10'>
                    <div className='prep-border' />
                </div>
            </div>
            <div className='columns '>
                <div className='column is-offset-1 is-10'>
                    <p className='p1 medium-weight'><HrbTranslate text='PrepareTask.Step2' /></p>
                    <p className='p2 step-content'><HrbTranslate text='PrepareTask.Step2Desc1' /></p>
                    <p className='p2 step-content'><HrbTranslate text={province === 'QC' ? 'PrepareTask.Step2Desc2QC' : 'PrepareTask.Step2Desc2'} /></p>
                    <p className='p2 step-content'><HrbTranslate text='PrepareTask.Step2Desc3' /></p>
                    {props.ticket &&
                    <Fragment>
                        <div className='step-center-container'>
                            <Button
                                elementId='prepare-manage-btn'
                                labelKey='DocUploadTask.ViewManage'
                                buttonStyles='secondary'
                                onClick={showExplorer}
                                disabled={baseStatus === TaskStatus.COMPLETE || !ticketIsOpen}
                            />
                            <Button
                                elementId='prepare-upload-btn'
                                labelKey='PrepareTask.UploadDocs'
                                buttonStyles='secondary'
                                onClick={showUploader}
                                disabled={baseStatus === TaskStatus.COMPLETE || !ticketIsOpen}
                            />
                        </div>                        
                        {hasUploaderError &&
                            <TaskErrorText errorKey='PrepareTask.ErrorUploader' />
                        }
                        </Fragment>
                    }
                    <div className='prep-border' />
                </div>
            </div>
            <div className='columns prep-section'>
                <div className='column is-offset-1 is-10'>
                    <p className='p1 medium-weight'><HrbTranslate text='PrepareTask.Step3' /></p>
                </div>
            </div>
            <PrepareAdditionalDocuments ticket={props.ticket} onSaveCompletion={saveCompletion}
                hasUploadedFiles={hasUploadedFiles} disabled={!ticketIsOpen} onSavePending={onSavePending} />
            <div className='columns prep-section'>
                <div className='column is-offset-1 is-10'>
                    <div className='prep-border' />
                </div>
            </div>
            <div className='columns prep-section'>
                <div className='column is-offset-1 is-10'>
                    <p className='p1 medium-weight'><HrbTranslate text='PrepareTask.Step4' /></p>
                    <p className='p2 step-content'><HrbTranslate text='PrepareTask.Step4Desc' /></p>
                </div>
            </div>
            <div className='columns prep-section'>
                <div className='column is-offset-1 is-10'>                    
                    {props.ticket &&
                    <div className='step-center-container'>
                        <Button
                            elementId='prepare-done-btn'
                            labelKey='PrepareTask.Step4ReviewBtn'
                            buttonStyles='primary'
                            onClick={handleReviewAction}
                            disabled={(baseStatus === TaskStatus.COMPLETE || !hasUploadedFiles || interviewStatus !== TaskStatus.COMPLETE || !ticketIsOpen)}
                            loading={isCompleting}
                            />                                                    
                    </div>
                    }
                    {hasCompletionError && !completionErrorType &&
                        <TaskErrorText errorKey='PrepareTask.ErrorSubmit' />
                    }
                    {hasCompletionError && completionErrorType &&
                        <ExtraMissingFilesError completionErrorType={completionErrorType}
                            extraFiles={extraFiles} missingFiles={missingFiles} task='prepare' />
                    }
                    <WorkflowTaskResetModal
                        isOpen={resetModalShowing} status={TaskStatus.IN_PROGRESS} onCancel={handleHideModal}
                        onReset={handleReset} titleKey="PrepareTask.ResetModal.Title"
                        resetButtonKey="PrepareTask.ResetModal.Btn"
                        contentKeys={['PrepareTask.ResetModal.Content']} />
                    {baseStatus === TaskStatus.COMPLETE && ticketIsOpen &&
                    <p className='p2 step-content'>
                        <HrbTranslate text='PrepareTask.Step4Reset' params={{ ResetLink: createResetLink() }}/>
                    </p>
                    }
                </div>
            </div>
            <DoxExplorer ticket={props.ticket} show={explorerShowing}
                onClose={onExplorerClose}
            />
            <DoxUploader ticket={props.ticket} show={uploaderOpen}
                layoutType={'desktop'} onUploadsComplete={onUploadsComplete} onClose={onCloseUploader}
            />
        </WorkflowTask>
    );
}

PrepareReturnTask.whyDidYouRender = false;

PrepareReturnTask.propTypes = {
    ticket: PropTypes.object
}

export default PrepareReturnTask